// AIDEV-NOTE: Container-header validation shared by the browser recorder and the
// server ingestion guard. Pure byte inspection so it runs in both places.
//
// Why this exists: MediaRecorder emits an *initialization segment* in its very
// first chunk (EBML header for WebM/Matroska, `ftyp`+`moov` for fragmented MP4).
// If chunk 0 is lost, the assembled file starts at a media fragment instead —
// still large, still the right mime type, but undecodable. AssemblyAI reports
// "Transcoding failed. File type application/octet-stream (data)". Five
// production sessions were lost this way between 2026-07-13 and 2026-10-21.
// See docs/runbooks/live-recording-container-integrity.md.

/** Bytes needed to classify a container. 12 covers EBML magic and the ftyp box. */
export const CONTAINER_SNIFF_BYTES = 12;

const EBML_MAGIC = [0x1a, 0x45, 0xdf, 0xa3];
/** Matroska Cluster ID — a WebM that begins here lost its EBML header. */
const MATROSKA_CLUSTER_ID = [0x1f, 0x43, 0xb6, 0x75];
const FTYP_BOX_TYPE = [0x66, 0x74, 0x79, 0x70]; // "ftyp"
const MOOF_BOX_TYPE = [0x6d, 0x6f, 0x6f, 0x66]; // "moof"
const ID3_TAG = [0x49, 0x44, 0x33]; // "ID3"
const RIFF_TAG = [0x52, 0x49, 0x46, 0x46]; // "RIFF"
const OGG_TAG = [0x4f, 0x67, 0x67, 0x53]; // "OggS"
const FLAC_TAG = [0x66, 0x4c, 0x61, 0x43]; // "fLaC"

export type MediaContainerKind =
    | "webm"
    | "mp4"
    | "mp3"
    | "wav"
    | "ogg"
    | "flac"
    | "aac"
    | "headerless-webm"
    | "headerless-mp4"
    | "unknown";

export interface MediaContainerVerdict {
    kind: MediaContainerKind;
    /**
     * True when the leading bytes match a container we positively recognize.
     *
     * `false` means only "not recognized here" — for `unknown` that is an
     * absence of evidence, not evidence of corruption. Use
     * {@link isDefinitelyHeaderless} to decide whether to reject.
     */
    playable: boolean;
    /** Lowercase hex of the inspected prefix, for diagnostics. Never audio content. */
    prefixHex: string;
}

function matchesAt(
    bytes: Uint8Array,
    offset: number,
    sequence: number[],
): boolean {
    if (bytes.length < offset + sequence.length) {
        return false;
    }
    return sequence.every((byte, index) => bytes[offset + index] === byte);
}

// AIDEV-NOTE: MPEG-1/2 audio frame sync — 11 set bits, for MP3s with no ID3 tag.
function isMpegFrameSync(bytes: Uint8Array): boolean {
    return (
        bytes.length >= 2 &&
        bytes[0] === 0xff &&
        (bytes[1] & 0xe0) === 0xe0 &&
        // Exclude ADTS (MPEG-4 AAC), matched separately below.
        (bytes[1] & 0x16) !== 0x10
    );
}

// AIDEV-NOTE: ADTS AAC frame sync — 0xFFF followed by MPEG-4 layer bits of 00.
function isAdtsFrameSync(bytes: Uint8Array): boolean {
    return (
        bytes.length >= 2 &&
        bytes[0] === 0xff &&
        (bytes[1] & 0xf6) === 0xf0
    );
}

function toHex(bytes: Uint8Array): string {
    return Array.from(bytes)
        .map((byte) => byte.toString(16).padStart(2, "0"))
        .join("");
}

/**
 * Classify a media file from its leading bytes.
 *
 * Pass at least {@link CONTAINER_SNIFF_BYTES}; fewer is tolerated and simply
 * yields a less specific verdict rather than throwing.
 */
export function inspectMediaContainer(
    bytes: Uint8Array,
): MediaContainerVerdict {
    const prefix = bytes.subarray(0, CONTAINER_SNIFF_BYTES);
    const prefixHex = toHex(prefix);

    if (matchesAt(bytes, 0, EBML_MAGIC)) {
        return { kind: "webm", playable: true, prefixHex };
    }
    // ISO-BMFF: a 4-byte big-endian box size followed by the box type at offset 4.
    if (matchesAt(bytes, 4, FTYP_BOX_TYPE)) {
        return { kind: "mp4", playable: true, prefixHex };
    }
    if (matchesAt(bytes, 0, ID3_TAG) || isMpegFrameSync(bytes)) {
        return { kind: "mp3", playable: true, prefixHex };
    }
    if (matchesAt(bytes, 0, RIFF_TAG)) {
        return { kind: "wav", playable: true, prefixHex };
    }
    if (matchesAt(bytes, 0, OGG_TAG)) {
        return { kind: "ogg", playable: true, prefixHex };
    }
    if (matchesAt(bytes, 0, FLAC_TAG)) {
        return { kind: "flac", playable: true, prefixHex };
    }
    if (isAdtsFrameSync(bytes)) {
        return { kind: "aac", playable: true, prefixHex };
    }
    if (matchesAt(bytes, 0, MATROSKA_CLUSTER_ID)) {
        return { kind: "headerless-webm", playable: false, prefixHex };
    }
    if (matchesAt(bytes, 4, MOOF_BOX_TYPE)) {
        return { kind: "headerless-mp4", playable: false, prefixHex };
    }
    return { kind: "unknown", playable: false, prefixHex };
}

/**
 * True only for containers we can positively identify as having lost their
 * initialization segment.
 *
 * `unknown` is deliberately excluded: AssemblyAI accepts more formats than this
 * classifier enumerates, so an unrecognized prefix must never be reported as
 * corruption on its own. Callers that know the media can only be WebM or MP4 —
 * a browser live recording — should additionally reject `unknown` themselves.
 */
export function isDefinitelyHeaderless(
    verdict: MediaContainerVerdict,
): boolean {
    return (
        verdict.kind === "headerless-webm" || verdict.kind === "headerless-mp4"
    );
}

/** True when the bytes begin with a WebM/Matroska EBML header. */
export function startsWithEbmlHeader(bytes: Uint8Array): boolean {
    return matchesAt(bytes, 0, EBML_MAGIC);
}

/** True when the bytes begin with an ISO-BMFF `ftyp` box (MP4 init segment). */
export function startsWithFtypBox(bytes: Uint8Array): boolean {
    return matchesAt(bytes, 4, FTYP_BOX_TYPE);
}

/**
 * True when the bytes carry an initialization segment for a recognized
 * container. Used as the ingestion gate before handing media to transcription.
 */
export function hasInitializationSegment(bytes: Uint8Array): boolean {
    return inspectMediaContainer(bytes).playable;
}
